import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SectionInfoBox from '../../../components/SectionInfoBox';
import { API_BASE } from '../../../utils/api';

interface DeveloperSpotlightProps {
    project: any;
    getImageUrl: (url: string) => string;
}

const DeveloperSpotlight: React.FC<DeveloperSpotlightProps> = ({ project, getImageUrl }) => {
    const isBroker = project.developer?.role === 'broker';
    const isOwner = project.developer?.role === 'owner';
    const devName = isBroker 
        ? (project.developer?.name || 'Trusted Broker') 
        : isOwner
        ? (project.developer?.name || 'Trusted Owner')
        : (project.developerDetails?.name || 'Trusted Developer');
    const devInitial = devName.charAt(0).toUpperCase();

    const [allDevelopers, setAllDevelopers] = useState<any[]>([]);

    useEffect(() => {
        fetch(`${API_BASE}/developers`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setAllDevelopers(data);
                }
            })
            .catch(err => console.error("Error fetching developers:", err));
    }, []);

    const coDevNames = !isBroker && project.developerDetails?.coDeveloper
        ? project.developerDetails.coDeveloper.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];

    const completed = parseInt(project.developerDetails?.projectsCompleted || '0') || 0;
    const ongoing = parseInt(project.developerDetails?.projectsOngoing || '0') || 0;
    const upcoming = parseInt(project.developerDetails?.projectsUpcoming || '0') || 0;
    const showMetrics = !isBroker && !isOwner && (completed > 0 || ongoing > 0 || upcoming > 0);

    return (
        <div id="developer-details" className="card border border-gray-200 shadow-sm rounded-2xl mb-8 overflow-hidden scroll-mt-32">
            {/* 1. Header Block */}
            <div className="bg-white pt-1.5 pb-0 px-6 md:px-8 border-b border-gray-50">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center">
                        <h4 className="text-xl md:text-2xl font-bold text-black leading-tight">
                            {isOwner ? 'Owner Profile' : isBroker ? 'Submitted by Broker' : 'Developer Profile'}
                        </h4>
                        <SectionInfoBox text={project.sectionInfo?.developer} />
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                        {project.developer?._id && (
                            <Link
                                to={isOwner ? `/owner-profile/${project.developer._id}` : isBroker ? `/agent-page/${project.developer._id}` : `/developer-page/${project.developer._id}`}
                                className="btn-shimmer inline-flex items-center justify-center px-3.5 py-2 bg-white border-2 border-[#001b4a] rounded-lg text-[11px] font-bold text-[#001b4a] hover:bg-slate-50 hover:text-black hover:border-black transition-all hover:scale-105 active:scale-95 shadow-lg"
                            >
                                <span>Explore profile</span>
                            </Link>
                        )}
                    </div>
                </div>
                <div className="w-12 h-0.5 bg-[#D4A017] rounded-full mb-1"></div>
            </div>

            <div className="bg-white p-2.5 md:p-3.5 lg:p-4.5 flex flex-col md:flex-row items-center justify-start gap-4 md:gap-6 lg:gap-8">
                
                {/* 2. Left Tier: Logo / Profile Photo */}
                <div className="relative group flex-shrink-0">
                    <div className="w-32 h-32 md:w-40 md:h-40 flex items-center justify-center overflow-hidden relative">
                        {isBroker || isOwner ? (
                            project.developer?.avatar ? (
                                <img
                                    src={getImageUrl(project.developer.avatar)}
                                    className="w-full h-full object-cover rounded-xl"
                                    alt={devName}
                                />
                            ) : (
                                <div className="w-full h-full rounded-xl flex items-center justify-center text-white text-3xl font-black" style={{ background: 'linear-gradient(135deg, #05144b 0%, #4c1d95 100%)' }}>
                                    {devInitial}
                                </div>
                            )
                        ) : project.developerDetails?.logo ? (
                            <img
                                src={getImageUrl(project.developerDetails.logo)}
                                className="w-full h-full object-contain"
                                alt={devName}
                            />
                        ) : (
                            <div className="w-full h-full rounded-xl flex items-center justify-center text-white text-3xl font-black" style={{ background: 'linear-gradient(135deg, #05144b 0%, #4c1d95 100%)' }}>
                                {devInitial}
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. Middle Tier: Info Strip */}
                <div className="flex-grow flex flex-col items-center md:items-start gap-4">
                    <div className="flex flex-col items-center md:items-start">
                        <h4 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-none mb-2">
                            {devName}
                        </h4>
                        <div className="flex flex-wrap gap-2 items-center">
                            {isBroker ? (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 rounded-full border border-blue-100/50">
                                    <span className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></span>
                                    <span className="text-[10px] font-medium text-blue-700 leading-none">Broker Partner</span>
                                </div>
                            ) : isOwner ? (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 rounded-full border border-emerald-100/50">
                                    <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></span>
                                    <span className="text-[10px] font-medium text-emerald-700 leading-none">Property Owner</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 rounded-full border border-emerald-100/50">
                                    <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></span>
                                    <span className="text-[10px] font-medium text-emerald-700 leading-none">A Grade Partner</span>
                                </div>
                            )}
                            {project.developerDetails?.coDeveloper && (
                                <div className="flex items-center gap-1.5 px-2.5 py-0.5 bg-purple-50 rounded-full border border-purple-100/50 text-[10px] font-medium text-purple-800">
                                    <i className="fa-solid fa-handshake"></i>
                                    <span><strong className="font-bold">Co-Developer(s):</strong> {project.developerDetails.coDeveloper}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Integrated Metric Row or Contact Us Button */}
                    {showMetrics && (
                        <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-8 gap-y-4 px-6 py-4 bg-gray-50/50 rounded-2xl border border-gray-100/80 w-full lg:w-max mt-2">
                            {[
                                { icon: 'fa-calendar-check', value: project.developerDetails?.projectsSince || '2012', label: 'Since' },
                                { icon: 'fa-building-circle-check', value: project.developerDetails?.projectsCompleted || '12', label: 'Delivered' },
                                { icon: 'fa-screwdriver-wrench', value: project.developerDetails?.projectsOngoing || '5', label: 'Ongoing' },
                                { icon: 'fa-map-location-dot', value: project.developerDetails?.projectsUpcoming || '4', label: 'Upcoming' }
                            ].map((stat, i) => (
                                <div key={i} className="flex items-center gap-3 group">
                                    <div className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center text-[#D4A017] group-hover:scale-110 transition-transform">
                                        <i className={`fa-solid ${stat.icon} text-lg`}></i>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-2xl font-black text-black leading-none">{stat.value}{stat.label !== 'Since' && '+'}</span>
                                        <span className="text-[13px] font-bold text-black leading-none mt-1.5">{stat.label}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Co-Developer Row */}
            {coDevNames.length > 0 && (
                <div className="bg-gray-50/30 border-t border-gray-100 px-6 py-4 md:px-8 flex flex-col gap-3">
                    <span className="text-[13px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 flex-shrink-0">
                        <i className="fa-solid fa-handshake text-purple-600"></i> Co-Developer(s)
                    </span>
                    <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 w-full">
                        {coDevNames.map((name: string, index: number) => {
                            const devObj = allDevelopers.find(d => d.name.toLowerCase() === name.toLowerCase());
                            return (
                                <div key={index} className="flex items-center gap-2.5 bg-white border border-gray-200/80 rounded-full px-3.5 py-2 shadow-sm transition-all hover:shadow hover:border-purple-300 w-full md:w-auto">
                                    {devObj?.logo ? (
                                        <img
                                            src={getImageUrl(devObj.logo)}
                                            alt={name}
                                            className="w-7 h-7 rounded-full object-contain bg-slate-50 p-0.5 border border-gray-100"
                                        />
                                    ) : (
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-black uppercase">
                                            {name.charAt(0)}
                                        </div>
                                    )}
                                    <span className="text-[13px] font-bold text-black truncate">{name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeveloperSpotlight;
